/**
 * The front desk shell: brand sidebar, header with today's date and the sync
 * pill, and the screen itself.
 *
 * The date is in the header on purpose. Every decision on this system —
 * expired or not, when a renewal starts — depends on what day it is, and the
 * clock is server-corrected. Showing it means staff can spot a wrong date
 * immediately instead of discovering it in a month of bad end dates.
 */
import { NavLink, useLocation } from 'react-router-dom';
import SyncIndicator from './SyncIndicator';
import { formatLongDate, todayISO } from '@shared/dates';
import { useEffect, useState } from 'react';

const NAV = [
  { to: '/', label: 'Dashboard', icon: '▤', end: true },
  { to: '/new', label: 'New subscription', icon: '＋', end: false },
  { to: '/walk-in', label: 'Walk-in', icon: '⚡', end: true },
  { to: '/walk-ins', label: 'Walk-in history', icon: '◷', end: false },
  { to: '/members', label: 'Members', icon: '☰', end: false },
  { to: '/renewals', label: 'Renewals', icon: '↻', end: false },
  { to: '/settings', label: 'Settings', icon: '⚙', end: false },
];

export default function AppShell({
  children,
  onLock,
}: {
  children: React.ReactNode;
  onLock: () => void;
}) {
  const location = useLocation();
  const [today, setToday] = useState(todayISO());

  // Roll the date over without a reload — the PC stays on for weeks.
  useEffect(() => {
    const id = setInterval(() => setToday(todayISO()), 60_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex min-h-screen bg-ink">
      {/* Sidebar */}
      <aside className="no-print sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r-2 border-brand bg-black md:flex">
        <div className="flex items-center gap-3 border-b border-edge px-6 py-5">
          <img src="/logo.png" alt="SK Martial Arts Academy" className="h-11 w-auto" />
          <div>
            <div className="font-display text-lg leading-none tracking-display">SK</div>
            <div className="text-[10px] uppercase tracking-button text-white/40">Front Desk</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex min-h-touch items-center gap-3 rounded-btn px-4 text-sm font-bold uppercase
                 tracking-button transition-all duration-brand ${
                   isActive
                     ? 'bg-brand text-paper'
                     : 'text-white/60 hover:bg-surface hover:text-paper'
                 }`
              }
            >
              <span className="w-5 text-center text-base">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-edge p-3">
          <button
            onClick={onLock}
            className="flex min-h-touch w-full items-center gap-3 rounded-btn px-4 text-sm
                       font-bold uppercase tracking-button text-white/50
                       transition-all duration-brand hover:bg-surface hover:text-paper"
          >
            <span className="w-5 text-center text-base">🔒</span>
            Lock
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header — black 95% + blur + red underline, same as the site's navbar */}
        <header className="no-print sticky top-0 z-40 border-b-2 border-brand bg-black/95 backdrop-blur-md">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div>
              <div className="text-[10px] uppercase tracking-button text-white/40">Today</div>
              <div className="text-lg font-semibold leading-tight">{formatLongDate(today)}</div>
            </div>
            <SyncIndicator />
          </div>

          {/* Mobile nav — the front desk PC is the primary target, but a manager
              glancing at a tablet should not hit a dead end. */}
          <nav className="flex gap-1 overflow-x-auto border-t border-edge px-3 py-2 md:hidden">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-btn px-3 py-2 text-xs font-bold uppercase tracking-button ${
                    isActive ? 'bg-brand text-paper' : 'text-white/50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </header>

        <main key={location.pathname} className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
}
